// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL MENÚ DEL PANEL DE PLATAFORMA — QUÉ SECCIÓN VIVE EN QUÉ RUTA, Y CUÁL SE PINTA
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  QUÉ PROBLEMA RESUELVE. Estando dentro del panel de plataforma, el menú lateral era el de la
//  CLÍNICA: pacientes, agenda, cobros. Nada de eso tiene que ver con dar de alta clínicas o editar
//  el texto legal que firman los pacientes de todas. Y la pantalla era un scroll de diez paneles
//  apilados, así que ni se podía enlazar a uno ni se podía dar acceso a uno sin dar el resto.
//
//  ── ESTE FICHERO ES DATOS, NO PANTALLA ───────────────────────────────────────────────────────
//  Aquí vive SOLO la correspondencia sección -> ruta -> icono, y la función que arma el menú. El
//  layout (components/PlataformaLayout.jsx) lo pinta y las rutas (App.jsx) lo montan. Está separado
//  a propósito por dos motivos:
//   1. una guardia puede EJECUTARLO (es JavaScript llano, sin JSX) y comprobar qué sale con unas
//      secciones y con otras, que es la única forma de medir "no se pinta lo que no se puede";
//   2. el día que se añada una sección al catálogo del servidor, el sitio que hay que tocar es UNO.
//
//  ── EL NOMBRE Y EL ORDEN LOS PONE EL SERVIDOR ────────────────────────────────────────────────
//  `GET /api/superadmin/mis-secciones` devuelve el catálogo entero (clave, label, categoría,
//  descripción) y las categorías con su orden. Aquí NO se vuelve a escribir ni un nombre: si se
//  escribieran en los dos sitios, un día dirían cosas distintas. Lo único que se hace con el texto
//  del servidor es QUITARLE EL PREFIJO DE SU PROPIA CATEGORÍA cuando lo lleva —bajo la cabecera
//  "CLÍNICAS", la entrada "Clínicas: ver el registro" se lee "Ver el registro"—, y eso es una
//  derivación de la cadena del servidor, no una segunda definición: el texto completo sigue a la
//  vista en el `title` de la entrada.
//
//  ── LO QUE NO PINTA ENTRADA, Y POR QUÉ ───────────────────────────────────────────────────────
//  Cuatro de las secciones del catálogo NO son pantallas (ver SIN_ENTRADA_PROPIA). Que no
//  aparezcan en el menú no es un olvido: una entrada de menú es una promesa de que ahí se llega a
//  algo, y enseñar una puerta que da 403 solo consigue que alguien pulse, no entienda por qué no
//  puede y de paso se entere de que existe.

// Sección del catálogo del servidor -> ruta de la aplicación. La ruta es la MISMA cadena que monta
// App.jsx (rutas hijas absolutas), para que las dos se puedan cruzar en una guardia.
export const RUTA_POR_SECCION = {
  clinicas: '/superadmin/clinicas',
  // B-602 · liberar o reasignar la dirección pública del formulario de una clínica. Sección propia,
  // porque decide a qué clínica le llegan los pacientes que envía un profesional.
  direcciones: '/superadmin/direcciones',
  // (`datos_clinica` ya no tiene pantalla propia: desde el 1-oct-2026, B-998, se entra en una clínica desde su tarjeta
  // en Clínicas. Ver SIN_ENTRADA_PROPIA.)
  demos: '/superadmin/demos',
  // t612 · B-2462 · qué se lee en biango.es y por dónde llega la gente (un recuento, sin cookies).
  visitas_web: '/superadmin/visitas-web',
  // B-942 · qué sistemas ofrece Biango a todas las clínicas (apagado aquí, desaparece en todas).
  sistemas: '/superadmin/sistemas',
  // (El espacio de cada clínica no tiene entrada: desde el 1-oct-2026 se lleva desde su Licencia, en Clínicas.)
  soporte: '/superadmin/soporte',
  // B-273 · atender la asistencia remota (el escritorio del PC de la clínica): su propia pantalla, no la del chat.
  asistencia_remota: '/superadmin/asistencia-remota',
  // 30-sep-2026 · los archivos que las clínicas envían al soporte, de TODAS (la descarga de cada asistencia vive en su
  // detalle; esta es la lista para quien tiene esta sección y no la atiende).
  archivos_soporte: '/superadmin/archivos-soporte',
  // M11 del migrador · las migraciones de datos de todas las clínicas y el catálogo de programas (MIGRADOR-ESPEC 7.2).
  migraciones: '/superadmin/migraciones',
  // t377 (22-oct-2026) · las incorporaciones: el control interno de cada clínica que entra (el resumen; la ficha, con su gestión).
  incorporaciones: '/superadmin/incorporaciones',
  // t408 (22-oct-2026) · «Mis horas»: el técnico apunta sus horas de cada incorporación, sin ver costes.
  incorporaciones_horas: '/superadmin/mis-horas',
  // t410 (23-oct-2026) · Biango Media: la publicidad en las teles de BiangoTV (mapa, anunciantes, campañas, informes, reparto).
  biango_media: '/superadmin/biango-media',
  // B-1003 · las sugerencias que proponen y votan las clínicas (moderar, contestar, unir repetidas).
  sugerencias_clinicas: '/superadmin/sugerencias',
  // t157 (9-oct-2026) · la salud de los trabajos automáticos (crons) de cada clínica: qué falla, desde cuándo y por qué.
  salud_trabajos: '/superadmin/salud-trabajos',
  ia_uso: '/superadmin/ia',
  asistente_lucia_coste: '/superadmin/asistente-lucia',
  whatsapp_precios: '/superadmin/whatsapp-precios',
  whatsapp_consumo: '/superadmin/whatsapp-consumo',
  // B-895 · emitir las facturas de Biango a las clínicas.
  facturacion_biango: '/superadmin/facturacion-biango',
  // L15 (4-oct-2026) · «Documentación legal» (antes «Consentimientos oficiales»); la dirección vieja redirige aquí (App.jsx).
  consentimientos_oficiales: '/superadmin/documentacion-legal',
  // 4-oct-2026 · la biblioteca de Biango: recomendaciones, medicamentos y plantillas de partida para las clínicas.
  biblioteca_biango: '/superadmin/biblioteca',
  centro_ayuda: '/superadmin/centro-ayuda',
  portal_contenidos: '/superadmin/portal',
  catalogo_casas: '/superadmin/catalogo-casas',
  avisos_equipo: '/superadmin/avisos',
  // 6-oct-2026 · la versión publicada de cada app del móvil en su tienda, la mínima y el enlace (el aviso de actualizar).
  apps_moviles: '/superadmin/versiones-apps',
  // t279 (17-oct-2026) · las funciones en desarrollo (2.0, 3.0…): encenderlas en producción, para todas o para alguna clínica.
  funciones_desarrollo: '/superadmin/funciones',
  equipo_plataforma: '/superadmin/equipo',
};

// Icono de cada entrada (nombres de components/ui.jsx). Aquí y no en el layout porque va atado a la
// sección, igual que la ruta: una sección nueva sin icono se ve en la misma guardia que la ruta.
export const ICONO_POR_SECCION = {
  clinicas: 'grid',
  direcciones: 'link',
  demos: 'calendarCheck',
  visitas_web: 'eye',
  sistemas: 'settings',
  soporte: 'chat',
  asistencia_remota: 'monitor',
  archivos_soporte: 'download',
  migraciones: 'transferir',
  incorporaciones: 'flag',
  incorporaciones_horas: 'clock',
  biango_media: 'trending',
  sugerencias_clinicas: 'star',
  ia_uso: 'barChart',
  asistente_lucia_coste: 'bot',
  whatsapp_precios: 'tag',
  whatsapp_consumo: 'whatsapp',
  facturacion_biango: 'facturacion',
  consentimientos_oficiales: 'doc',
  biblioteca_biango: 'folder',
  centro_ayuda: 'clipboard',
  portal_contenidos: 'video',
  catalogo_casas: 'paquete',
  avisos_equipo: 'megaphone',
  apps_moviles: 'tablet',
  salud_trabajos: 'pulso',
  funciones_desarrollo: 'flask',
  equipo_plataforma: 'users',
};

// Las secciones del catálogo que NO pintan entrada de menú, con el motivo escrito. Una sección que
// no esté ni aquí ni en RUTA_POR_SECCION es una sección que se ha quedado sin sitio, y eso lo
// detecta la guardia: se elige, no se olvida.
export const SIN_ENTRADA_PROPIA = {
  clinicas_gestion:
    'No es una pantalla: son los botones de dentro del registro de clínicas (alta, licencia, '
    + 'aprovisionar, primer usuario, datos demo). Quien la tenga sin tener "clínicas" no puede ni '
    + 'listar clínicas —su GET responde 403—, así que una entrada propia le abriría una pantalla vacía.',
  credenciales:
    'Tampoco es una pantalla: son los tres bloques de dentro del modal de una clínica (WhatsApp, '
    + 'la llave de sus agentes, correo y el TPV de Biango). Sin el registro de clínicas no hay desde dónde abrirlos.',
  incorporaciones_gestion:
    'No es una pantalla propia: es la lista de clínicas, la ficha de cada incorporación (su parte de horas, incidencias, '
    + 'gastos y costes) y los ajustes, dentro de Incorporaciones. Quien la tenga sin «incorporaciones» no puede ni abrir esa '
    + 'pantalla, así que una entrada propia le llevaría a una puerta cerrada.',
  datos_clinica:
    'No es una pantalla desde el 1-oct-2026 (B-998): es el botón «Entrar en esta clínica» de cada tarjeta del '
    + 'registro de clínicas (y el de una conversación de soporte). La pantalla de pedir permiso, '
    + '/superadmin/acceso-clinicas, se ha retirado y su dirección lleva a Clínicas.',
};

// Quita el prefijo de la categoría cuando el nombre del servidor lo lleva ("Clínicas: ver el
// registro" bajo la cabecera CLÍNICAS se lee "Ver el registro"). Si no lo lleva, se devuelve tal cual.
export function etiquetaCorta(label, labelCategoria) {
  const l = String(label || '');
  const c = String(labelCategoria || '');
  if (!c) return l;
  const pref = c + ': ';
  if (l.toLowerCase().startsWith(pref.toLowerCase()) && l.length > pref.length) {
    const resto = l.slice(pref.length);
    return resto.charAt(0).toUpperCase() + resto.slice(1);
  }
  return l;
}

// ── LAS PÁGINAS DE DENTRO DE UNA SECCIÓN (B-1251, 12-oct-2026: lo que se configura una vez, detrás de su botón) ─────
// El dueño: nunca pestañas dentro de pestañas. Lo que se mira cada día es la pantalla de la sección; lo que se configura una
// vez va en una página de dentro, con su botón arriba a la derecha y la vuelta («‹ Facturas de Biango») al entrar. Cada una
// es una dirección debajo de la de su sección y se monta en App.jsx con LA MISMA sección (sus guardias lo comprueban): no
// son secciones nuevas ni abren nada que la sección no abriera ya. Las pinta el layout (components/PlataformaLayout.jsx).
// `VUELTA_DESDE_DENTRO`: secciones cuya pantalla ya lleva a sus páginas de dentro (la Biblioteca, una fila por tipo): sin
// botón arriba, y la vuelta en cualquier dirección de debajo.
export const SUBPAGINAS = Object.freeze({
  facturacion_biango: Object.freeze([
    Object.freeze({ to: '/superadmin/facturacion-biango/ajustes', label: 'Planes y ajustes' }),
    Object.freeze({ to: '/superadmin/facturacion-biango/clinicas', label: 'El precio de cada clínica' }),
  ]),
  demos: Object.freeze([
    Object.freeze({ to: '/superadmin/demos/presupuestos', label: 'Presupuestos para clínicas' }),
    Object.freeze({ to: '/superadmin/demos/cookies', label: 'Cookies de biango.es' }),
    // t597 · B-2436 · el Instagram de Biango (@biango_dental): la palabra clave, el dossier y la demo por privado.
    Object.freeze({ to: '/superadmin/demos/instagram', label: 'Instagram de Biango' }),
    Object.freeze({ to: '/superadmin/demos/ajustes', label: 'Ajustes de la agenda' }),
  ]),
  migraciones: Object.freeze([Object.freeze({ to: '/superadmin/migraciones/catalogo', label: 'Catálogo de programas' })]),
  // t340 (21-oct-2026) · la prueba de LucIA en 3D (el botón de soporte nuevo), antes de ponerlo en la aplicación.
  funciones_desarrollo: Object.freeze([Object.freeze({ to: '/superadmin/funciones/lucia-3d', label: 'LucIA en 3D' })]),
});
export const VUELTA_DESDE_DENTRO = Object.freeze({ biblioteca_biango: true });

// ── INICIO (B-1251, 12-oct-2026) ─────────────────────────────────────────────────────────────
// La primera entrada del menú y la pantalla a la que se llega al entrar: lo que pide atención hoy. NO es una sección
// del catálogo y no concede nada: junta en una pantalla lo que ya se ve en las secciones que esa persona tiene, con
// las mismas peticiones de cada una (el servidor las vuelve a exigir una a una). Por eso no tiene clave en el
// servidor ni un nombre que pueda decir otra cosa en dos sitios. Sale solo si hay alguna entrada debajo: a quien no
// tiene ninguna sección no se le pinta un Inicio vacío.
export const RUTA_INICIO = '/superadmin/inicio';
export const INICIO = Object.freeze({
  tipo: 'entrada', clave: 'inicio', to: RUTA_INICIO, icono: 'inicio', label: 'Inicio', labelLargo: 'Inicio',
  descripcion: 'Lo que pide atención hoy en las secciones que tienes', tambien: [], dentro: [], vueltaDesdeDentro: false,
});

// EL MENÚ. Entra la respuesta de /superadmin/mis-secciones tal cual y sale la lista que se pinta:
// Inicio, y debajo las cabeceras de categoría con sus entradas, en el orden que manda el servidor (categorías por
// su lista; dentro de cada una, por su `ordenMenu` y, sin él, por el orden del catálogo). Solo salen las secciones
// CONCEDIDAS y que tienen pantalla; y una cabecera de categoría que se queda sin ninguna entrada debajo tampoco sale
// — si no, alguien de soporte vería "DINERO" seguido de nada.
// EL NOMBRE de cada entrada es el corto que manda el servidor (`menu`: «Consultas», «Facturas de Biango»); sin él, el
// largo sin el prefijo de su categoría. El largo sigue viajando (`labelLargo`, el `title` de la entrada).
// DENTRO DE OTRA (`menuDentroDe`, del servidor): Precios de WhatsApp se abre desde WhatsApp. Con la sección de
// fuera concedida no lleva entrada propia y su dirección enciende la de fuera (`tambien`); sin ella, sale sola con su
// nombre, porque quien solo tiene los precios tiene que poder llegar.
// `lecturaDeDiagnostico` = el panel esta en MODO AVERIA y el servidor ha dado la capacidad de
// MIRARLO (`lectura_panel`). No es una lista de secciones ni se comporta como una: durante la
// averia el servidor no concede NINGUNA —`puede()` dice que no a todas las claves— y lo unico que
// pasa es que las LECTURAS del panel siguen contestando 200. Si el menu se pintara con `secciones`
// a secas, la averia dejaria una pantalla en blanco y arreglarla obligaria a entrar por SSH: justo
// lo que la degradacion existe para evitar. Los botones de escribir de cada pantalla siguen mirando
// `pleno`/`secciones`, que vienen vacios, asi que salen apagados.
export function construirMenu({ catalogo, categorias, secciones, lecturaDeDiagnostico = false } = {}) {
  const tiene = new Set(
    lecturaDeDiagnostico
      ? (Array.isArray(catalogo) ? catalogo : []).map((s) => s.clave)
      : (Array.isArray(secciones) ? secciones : []));
  const cat = Array.isArray(catalogo) ? catalogo : [];
  const conPantalla = (s) => !!(s && tiene.has(s.clave) && RUTA_POR_SECCION[s.clave]);
  const porClave = new Map(cat.map((s) => [s.clave, s]));
  // Va dentro de otra que también tiene concedida: no pinta entrada propia.
  const vaDentro = (s) => !!(s.menuDentroDe && conPantalla(porClave.get(s.menuDentroDe)));
  const grupos = Array.isArray(categorias) && categorias.length
    ? categorias
    // Sin categorías (respuesta vieja o recortada) no se inventa ninguna: se pinta todo seguido.
    : [{ clave: null, label: '' }];
  const orden = (s) => (Number.isFinite(Number(s.ordenMenu)) && s.ordenMenu !== null ? Number(s.ordenMenu) : 1000 + cat.indexOf(s));
  const salida = [];
  for (const g of grupos) {
    const entradas = cat
      .filter((s) => (g.clave == null || s.categoria === g.clave) && conPantalla(s) && !vaDentro(s))
      .sort((a, b) => orden(a) - orden(b))
      .map((s) => ({
        tipo: 'entrada',
        clave: s.clave,
        to: RUTA_POR_SECCION[s.clave],
        icono: ICONO_POR_SECCION[s.clave] || null,
        label: s.menu ? String(s.menu) : etiquetaCorta(s.label, g.label),
        labelLargo: s.label,
        descripcion: s.descripcion || '',
        // Las secciones que van dentro de esta: su botón en la pantalla de fuera (`dentro`, lo pinta el layout) y sus
        // direcciones, que encienden esta entrada en el menú (`tambien`).
        dentro: cat.filter((d) => d.menuDentroDe === s.clave && conPantalla(d))
          .map((d) => ({ clave: d.clave, to: RUTA_POR_SECCION[d.clave], label: d.menu ? String(d.menu) : String(d.label || d.clave) }))
          // Y sus páginas de dentro (las de configurar una vez): misma sección, así que salen con ella.
          .concat((SUBPAGINAS[s.clave] || []).map((x) => ({ clave: s.clave + ':' + x.to, to: x.to, label: x.label }))),
        vueltaDesdeDentro: VUELTA_DESDE_DENTRO[s.clave] === true,
      }))
      // `tambien`: solo las direcciones de las SECCIONES de dentro (las páginas de dentro ya cuelgan de la dirección de la suya).
      .map((e) => ({ ...e, tambien: e.dentro.filter((d) => !String(d.clave).includes(':')).map((d) => d.to) }));
    if (!entradas.length) continue;
    if (g.label) salida.push({ tipo: 'grupo', clave: g.clave, label: g.label });
    for (const e of entradas) salida.push(e);
  }
  if (salida.some((m) => m.tipo === 'entrada')) salida.unshift({ ...INICIO, tambien: [], dentro: [], vueltaDesdeDentro: false });
  return salida;
}

// ¿Esta entrada es la de la pantalla en la que se está? Su dirección o una de dentro («/superadmin/biblioteca/medicamentos»
// enciende Biblioteca), o la de una sección que va dentro de ella (`tambien`). Pura: la ejecuta su guardia.
export function entradaActiva(entrada, pathname) {
  const p = String(pathname || '');
  const casa = (to) => !!to && (p === to || p.startsWith(to + '/'));
  return !!entrada && (casa(entrada.to) || (Array.isArray(entrada.tambien) && entrada.tambien.some(casa)));
}

// La primera pantalla a la que llevar a alguien que entra por /superadmin sin decir a cuál: la primera entrada del
// menú que se le pinta. Desde B-1251 es Inicio para todo el que tenga alguna sección con pantalla (y Inicio solo le
// enseña lo de las suyas); sin ninguna, no hay a dónde llevarle.
export function primeraRuta(menu) {
  const e = (menu || []).find((x) => x.tipo === 'entrada');
  return e ? e.to : null;
}
